import { useMemo, useState } from 'react';
import { ChartPieSlice, Door, DoorOpen, Ruler } from '@phosphor-icons/react';
import { InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand } from '../../../../../components/immobilien/ui';
import { eur, qm } from '../../../../../lib/immobilien/format';
import { geschoss, nachGeschoss } from '../../../../../lib/immobilien/objektDetail';
import { useObjektSeite } from '../ObjektSeitenKontext';

/** Panel „Flächen“: Kennzahlen, Flächenverteilung, Geschosse und Tabelle. */
export function FlaechePanel() {
  const { uebersicht, kennzahlen: k, daten, zuEinheiten } = useObjektSeite();
  const [hover, setHover] = useState<string | null>(null);
  const etage = useMemo(() => new Map(daten.einheiten.map(e => [e.id, e.etage ?? null])), [daten.einheiten]);
  const mitFlaeche = uebersicht.filter(e => e.flaeche > 0);
  const summe = mitFlaeche.reduce((s, e) => s + e.flaeche, 0);
  const vermietet = mitFlaeche.filter(e => e.vermietet).reduce((s, e) => s + e.flaeche, 0);
  const geschosse = nachGeschoss(mitFlaeche, e => geschoss(etage.get(e.id), e.bezeichnung));

  if (uebersicht.length === 0) return <LeerZustand icon={Ruler} titel="Noch keine Einheiten" text="Lege Einheiten an, dann erscheinen hier die Flächen." />;

  const anteil = (f: number) => (summe > 0 ? `${Math.round((f / summe) * 1000) / 10} %` : '–');

  return (
    <>
      <KpiReihe>
        <KpiTile label="Gesamtfläche" wert={k.flaeche > 0 ? qm(k.flaeche) : '–'} sub={summe > 0 && Math.abs(summe - k.flaeche) > 0.5 ? `${qm(Math.round(summe * 100) / 100)} in Einheiten erfasst` : `${uebersicht.length} Einheiten`} icon={Ruler} ton="acc" />
        <KpiTile label="Vermietet" wert={qm(Math.round(vermietet * 100) / 100)} sub={summe > 0 ? `${Math.round((vermietet / summe) * 100)} % der Fläche` : '–'} icon={Door} ton="ok" />
        <KpiTile label="Leerstand" wert={qm(Math.round((summe - vermietet) * 100) / 100)} sub={`${k.frei} ${k.frei === 1 ? 'Einheit' : 'Einheiten'} frei`} icon={DoorOpen} ton="warn" />
        <KpiTile label="Ø je Einheit" wert={mitFlaeche.length ? qm(Math.round((summe / mitFlaeche.length) * 100) / 100) : '–'} sub={k.kaltProQm !== null ? `Ø ${eur(k.kaltProQm)} / m² kalt` : 'Keine Miete erfasst'} icon={ChartPieSlice} />
      </KpiReihe>

      {mitFlaeche.length === 0 ? (
        <LeerZustand icon={Ruler} titel="Keine Flächen erfasst" text="Bei den Einheiten sind noch keine Wohn- oder Nutzflächen eingetragen." />
      ) : (
        <>
          <InnenKarte>
            <KartenTitel rechts="Größe der Kachel = Anteil an der Gesamtfläche">Flächenverteilung</KartenTitel>
            <div className="flex flex-wrap" style={{ gap: 6 }}>
              {mitFlaeche.map(e => (
                <button key={e.id} type="button" onClick={() => zuEinheiten(e.id)} onMouseEnter={() => setHover(e.id)} onMouseLeave={() => setHover(null)}
                  className="flex flex-col justify-between text-left cursor-pointer min-w-0"
                  style={{
                    flexGrow: e.flaeche, flexBasis: Math.max(90, e.flaeche * 1.6), minHeight: 86, borderRadius: 16, padding: '10px 12px', border: 'none',
                    background: e.vermietet ? 'var(--acc-soft)' : 'var(--warn-soft)', color: 'var(--ink)',
                    boxShadow: hover === e.id ? 'inset 0 0 0 2px var(--acc)' : 'none', transition: 'box-shadow .2s',
                  }}>
                  <span className="flex flex-col min-w-0">
                    <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{e.bezeichnung}</span>
                    <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{e.vermietet ? e.mieter.join(', ') : 'Leerstand'}</span>
                  </span>
                  <span className="flex justify-between items-end" style={{ gap: 6 }}>
                    <span className="immo-zahl" style={{ fontSize: 15, fontWeight: 800 }}>{qm(e.flaeche)}</span>
                    <span className="immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)' }}>{anteil(e.flaeche)}</span>
                  </span>
                </button>
              ))}
            </div>
          </InnenKarte>

          <div className="flex flex-wrap" style={{ gap: 12 }}>
            {geschosse.length > 1 && (
              <InnenKarte style={{ flex: '1 1 300px' }}>
                <KartenTitel>Nach Geschoss</KartenTitel>
                {geschosse.map(g => (
                  <div key={g.geschoss} className="flex items-center" style={{ gap: 10 }}>
                    <span style={{ width: 82, fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>{g.geschoss}</span>
                    <div className="flex flex-1 min-w-0" style={{ gap: 4 }}>
                      {g.einheiten.map(e => (
                        <button key={e.id} type="button" title={e.bezeichnung} onClick={() => zuEinheiten(e.id)} onMouseEnter={() => setHover(e.id)} onMouseLeave={() => setHover(null)}
                          className="immo-zahl flex items-center justify-center cursor-pointer"
                          style={{ flexGrow: e.flaeche, height: 30, borderRadius: 8, border: 'none', fontSize: 11, fontWeight: 700, minWidth: 34,
                            background: hover === e.id ? 'var(--acc)' : e.vermietet ? 'var(--acc-soft)' : 'var(--warn-soft)', color: hover === e.id ? 'var(--acc-ink)' : 'var(--ink)' }}>
                          {Math.round(e.flaeche)}
                        </button>
                      ))}
                    </div>
                    <span className="immo-zahl" style={{ width: 80, textAlign: 'right', fontSize: 12, color: 'var(--ink3)' }}>{qm(Math.round(g.einheiten.reduce((s, e) => s + e.flaeche, 0)))}</span>
                  </div>
                ))}
              </InnenKarte>
            )}
            <InnenKarte style={{ flex: '1.4 1 360px' }} padding="12px 18px" gap={0}>
              <div className="grid" style={{ gridTemplateColumns: 'minmax(0,2fr) repeat(3,minmax(0,1fr))', gap: 10, padding: '8px 0', fontSize: 11, fontWeight: 700, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--line)' }}>
                <span>Einheit</span><span style={{ textAlign: 'right' }}>Fläche</span><span style={{ textAlign: 'right' }}>Anteil</span><span style={{ textAlign: 'right' }}>Kalt/m²</span>
              </div>
              {mitFlaeche.map(e => (
                <button key={e.id} type="button" onClick={() => zuEinheiten(e.id)} onMouseEnter={() => setHover(e.id)} onMouseLeave={() => setHover(null)}
                  className="grid items-center text-left cursor-pointer"
                  style={{ gridTemplateColumns: 'minmax(0,2fr) repeat(3,minmax(0,1fr))', gap: 10, padding: '10px 0', border: 'none', borderBottom: '1px solid var(--line)', background: hover === e.id ? 'var(--hover)' : 'transparent', fontSize: 13, color: 'var(--ink)' }}>
                  <span className="flex items-center min-w-0" style={{ gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: e.vermietet ? 'var(--acc)' : 'var(--warn)' }} />
                    <b className="truncate" style={{ fontWeight: 700 }}>{e.bezeichnung}</b>
                  </span>
                  <span className="immo-zahl" style={{ textAlign: 'right' }}>{qm(e.flaeche)}</span>
                  <span className="immo-zahl" style={{ textAlign: 'right', color: 'var(--ink2)' }}>{anteil(e.flaeche)}</span>
                  <span className="immo-zahl" style={{ textAlign: 'right', color: 'var(--ink2)' }}>{e.vermietet && e.kalt > 0 ? eur(Math.round((e.kalt / e.flaeche) * 100) / 100) : '–'}</span>
                </button>
              ))}
            </InnenKarte>
          </div>
        </>
      )}
    </>
  );
}
